import {
  Button,
  Checkbox,
  ErrorBoundary,
} from '@energybox/react-ui-library/dist/components';
import CardList, {
  CardListGroupHeader,
  CardListHeader,
  CardListRowData,
  CardListStatus,
  Cell,
} from '@energybox/react-ui-library/dist/components/CardList';
import {
  useViewportType,
  ViewportTypes,
} from '@energybox/react-ui-library/dist/hooks';
import {
  Incident,
  Locale,
  ParametersSwitch,
  ParametersSwitchOnOff,
  ResourcePath,
  ResourceType,
  SensorType,
  SentinelPriorityLevel,
  SentinelType,
  Site,
  SortDirection,
} from '@energybox/react-ui-library/dist/types';
import {
  classNames,
  determineTimeStringByTimezone,
  genericTableSort,
  global,
  isDefined,
  SORT_IGNORED_VALUES,
} from '@energybox/react-ui-library/dist/utils';
import parseISO from 'date-fns/parseISO';
import pathOr from 'ramda/src/pathOr';

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { openDialog } from '../../actions/dialog';
import { dismissNotification } from '../../actions/notifications';
import NotificationEnergyChartContainer from '../../containers/NotificationEnergyChartContainer';
import NotificationOperationalSensorChartContainer from '../../containers/NotificationOperationalSensorChartContainer';
import NotificationThermostatChartContainer from '../../containers/NotificationThermostatChartContainer';
import { IncidentsListPageType } from '../../containers/views/incidents/IncidentsListPage';
import useAppLocale from '../../hooks/useAppLocale';
import { useGetAllSites } from '../../hooks/useSites';
import { ApplicationState } from '../../reducers';
import { PathsById } from '../../reducers/resources';
import { doesNotificationHaveChartData } from '../../utils/notifications';
import IconSensorTypeFactory from '../icons/IconSensorTypeFactory';
import IncidentValueChip from '../IncidentValueChip';
import SensorResource from '../SensorResource';
import ShortenedSpan from '../ShortenedSpan';
import BigNumber from '../ui/BigNumber';
import BigNumberDivider from '../ui/BigNumberDivider';
import styles from './IncidentsCardList.module.css';

type Props = {
  siteId?: string;
  pageType: IncidentsListPageType;
  incidents: Incident[];
  listPageRef: React.RefObject<HTMLDivElement>;
};

const IncidentsCardList: React.FC<Props> = ({
  siteId,
  incidents,
  pageType,
  listPageRef,
}) => {
  ///*** Hooks ***///
  const dispatch = useDispatch();
  const isMobile = useViewportType() === ViewportTypes.MOBILE;
  const locale = useAppLocale();
  const sitesById = useGetAllSites();
  const resourcePathsById = useSelector<ApplicationState, PathsById>(
    ({ resources }) => {
      return resources.byId;
    }
  );
  //End Hooks

  ///*** Local vars + Functions ***///
  const incidentsCount = incidents.length;

  const openDismissModal = (notificationId: string) => {
    dispatch(
      openDialog({
        isOpen: true,
        title: 'Dismiss Notification',
        type: 'DISMISS_NOTIFICATION',
        targetId: notificationId,
        actionFn: ({ notificationId, comment }) =>
          dispatch(dismissNotification(notificationId, comment, siteId)),
      })
    );
  };

  const determineIconParameters = (
    incident: Incident
  ): ParametersSwitch | undefined => {
    if (incident.sentinelType === SentinelType.DOOR_SIREN_CHECK) {
      return {
        onOff:
          incident.resolved === false
            ? ParametersSwitchOnOff.OFF
            : ParametersSwitchOnOff.ON,
      };
    }
    return undefined;
  };

  const renderChart = (incident: Incident, timezone: string | undefined) => {
    const sensorId = incident.sensorParams?.[0]?.sensorId;
    const sensorType = incident.sensorParams?.[0]?.type;
    const equipmentId = incident.sensorParams?.[0]?.resourceId;

    switch (incident.sentinelType) {
      case SentinelType.ACTIVE_POWER_THRESHOLD: {
        return (
          <NotificationEnergyChartContainer
            siteId={siteId}
            sensorId={sensorId}
            equipmentId={equipmentId}
            locale={locale}
            featureIncident={incident}
          />
        );
      }

      case SentinelType.THERMOSTAT_LOCAL_OVERRIDE_CHECK: {
        return (
          <NotificationThermostatChartContainer featureIncident={incident} />
        );
      }

      case SentinelType.CUSTOMER_COMFORT:
      case SentinelType.TEMPERATURE_AND_DOOR:
      case SentinelType.BINARY_COUNTER:
      case SentinelType.BINARY:
      case SentinelType.HUMIDITY:
      case SentinelType.TEMPERATURE: {
        return (
          <NotificationOperationalSensorChartContainer
            sensorId={sensorId}
            sensorType={sensorType}
            locale={locale}
            featureIncident={incident}
            ianaTimeZoneCode={timezone}
          />
        );
      }

      default:
        return null;
    }
  };

  const normalNumberOfGridColumns =
    pageType === IncidentsListPageType.MULTI_SITE ? 20 : 18;
  const numberOfGridColumns = isMobile ? 12 : normalNumberOfGridColumns;
  ///*** End Local vars + Functions ***///

  ///*** CardList Data ***///
  const data: CardListRowData[] = incidents.map((incident) => {
    const site: Site | undefined = sitesById[incident.siteId];
    const siteTitle = site?.title || global.NOT_AVAILABLE;
    const siteTimezone: string | undefined = site?.timeZone;

    const sensorId = incident.sensorId ? incident.sensorId : undefined;
    const sensorType: SensorType = pathOr(
      undefined,
      ['sensorParams', 0, 'type'],
      incident
    );
    const sentinelType = incident.sentinelType;

    const hasChartData = doesNotificationHaveChartData(incident.sentinelType);

    const priorityLevel = incident.incidentPriority;

    const extraContent =
      sensorId && sensorType && hasChartData
        ? [
            <Cell
              width={String(numberOfGridColumns)}
              className={styles.extraContent}
            >
              <div className={styles.chartPadding}>
                <ErrorBoundary>
                  {renderChart(incident, siteTimezone)}
                </ErrorBoundary>
              </div>
              {isMobile && (
                <Button onClick={() => openDismissModal(incident.incidentId)}>
                  Dismiss
                </Button>
              )}
            </Cell>,
          ]
        : undefined;

    if (isMobile) {
      return {
        status: incident.incidentPriority.toLowerCase() as CardListStatus,
        key: `Incident-Card-${incident.incidentId}`,
        dataObject: incident,
        content: (
          <>
            <Cell width="6">
              <div className={styles.iconAndTitle}>
                <div className={styles[`icon--${priorityLevel.toLowerCase()}`]}>
                  <IconSensorTypeFactory
                    size={20}
                    id={incident.sentinelType}
                    parameters={determineIconParameters(incident)}
                  />
                </div>
                <div className={styles.sentinelTitle}>
                  {incident.sentinelTitle}
                </div>
              </div>
              <div className={styles.sentinelDescription}>
                <ShortenedSpan
                  maxStringLength={80}
                  content={incident.sentinelDescription}
                />
              </div>
            </Cell>
            <Cell width="3">
              {renderTime(incident.createdAt, locale, 'right', site?.timeZone)}
            </Cell>
            <Cell width="3">
              {renderTime(
                incident.recoveredAt,
                locale,
                'right',
                site?.timeZone
              )}
            </Cell>
            <Cell />
            <Cell width="6">{renderMostRecentValue(incident)}</Cell>
            <Cell
              width="6"
              cellAlign="right"
              className={styles.priorityBadgesContainer}
            >
              <div className={styles.priorityBadges}>
                {Object.keys(SentinelPriorityLevel)
                  .filter(
                    (level) => incident.notificationCountsByPriority[level] > 0
                  )
                  .map((level) => (
                    <div
                      className={classNames(
                        styles.badge,
                        styles[`badge--${level.toLowerCase()}`]
                      )}
                    >
                      {incident.notificationCountsByPriority[level]}
                    </div>
                  ))}
              </div>
            </Cell>
            <Cell width="13">
              <div className={styles.hr} />
            </Cell>
            <Cell width="10">
              {sensorId ? (
                <SensorResource
                  sensorId={sensorId}
                  sentinelType={sentinelType}
                  maxStringLength={80}
                  equipmentTitleClassName={styles.mobileEquipmentTitleClassName}
                  showSiteName
                />
              ) : (
                <span className={styles.mobileEquipmentTitleClassName}>
                  {global.NOT_AVAILABLE}
                </span>
              )}
            </Cell>
            <Cell
              width="3"
              cellAlign="right"
              className={styles.mobileResolvedTextContainer}
            >
              {renderStatus(incident.resolved)}
            </Cell>
          </>
        ),
        extraContent,
      };
    }

    return {
      status: incident.incidentPriority.toLowerCase() as CardListStatus,
      key: `Incident-Card-${incident.incidentId}`,
      dataObject: incident,
      content: (
        <>
          <Cell width="1">
            <div className={styles[`icon--${priorityLevel.toLowerCase()}`]}>
              <IconSensorTypeFactory
                size={20}
                id={incident.sentinelType}
                parameters={determineIconParameters(incident)}
              />
            </div>
          </Cell>
          <Cell width="3">
            <div>
              {sensorId ? (
                <SensorResource
                  sensorId={sensorId}
                  sentinelType={sentinelType}
                  maxStringLength={27}
                />
              ) : (
                global.NOT_AVAILABLE
              )}
            </div>

            {renderMostRecentValue(incident)}
          </Cell>
          {pageType === IncidentsListPageType.MULTI_SITE && (
            <Cell width="2">{siteTitle}</Cell>
          )}
          <Cell width="3">
            <div className={styles.sentinelTitle}>{incident.sentinelTitle}</div>
            <div className={styles.sentinelDescription}>
              <ShortenedSpan
                maxStringLength={80}
                content={incident.sentinelDescription || ''}
              />
            </div>
          </Cell>
          <Cell cellAlign="right" width="1">
            {incident.notificationCountsByPriority[SentinelPriorityLevel.LOW] ||
              global.NOT_AVAILABLE}
          </Cell>
          <Cell cellAlign="right" width="1">
            {incident.notificationCountsByPriority[
              SentinelPriorityLevel.MEDIUM
            ] || global.NOT_AVAILABLE}
          </Cell>
          <Cell cellAlign="right" width="1">
            {incident.notificationCountsByPriority[
              SentinelPriorityLevel.HIGH
            ] || global.NOT_AVAILABLE}
          </Cell>
          <Cell cellAlign="right" width="1">
            {incident.notificationCountsByPriority[
              SentinelPriorityLevel.CRITICAL
            ] || global.NOT_AVAILABLE}
          </Cell>
          <Cell cellAlign="right" width="2">
            {renderTime(
              incident.createdAt,
              locale,
              'right',
              site?.timeZone,
              true
            )}
          </Cell>
          <Cell cellAlign="right" width="2">
            {renderTime(
              incident.recoveredAt,
              locale,
              'right',
              site?.timeZone,
              true
            )}
          </Cell>
          <Cell cellAlign="center" width="2">
            {renderStatus(incident.resolved)}
          </Cell>
          <Cell width="1">
            <div className={styles.dismissCheckboxContainer}>
              <Checkbox
                //incidentId refers to the incident's most recent notificationId
                id={incident.incidentId}
                checked={isDefined(incident.dismissedAt)}
                disabled={isDefined(incident.dismissedAt)}
                onChange={() => openDismissModal(incident.incidentId)}
                size={20}
              />
            </div>
          </Cell>
        </>
      ),
      extraContent:
        sensorId && sensorType && hasChartData
          ? [
              <Cell
                width={
                  pageType === IncidentsListPageType.MULTI_SITE ? '20' : '18'
                }
              >
                <div className={styles.chartPadding}>
                  <ErrorBoundary>
                    {renderChart(incident, siteTimezone)}
                  </ErrorBoundary>
                </div>
              </Cell>,
            ]
          : undefined,
    };
  });
  ///*** CardList Data ***///

  ///*** CardList Headers ***///
  const groupHeaders: CardListGroupHeader[] = [
    {
      width: pageType === IncidentsListPageType.MULTI_SITE ? '9' : '7',
      content: '',
    },
    {
      width: '4',
      content: 'Notifications Sent',
      className: styles.groupHeader,
    },
    {
      width: '7',
      content: '',
    },
  ];
  const singleSiteHeader: CardListHeader<Incident>[] = [
    {
      width: '1',
      content: '',
    },
    {
      width: '3',
      content: 'Equipment/Space',
    },
    {
      width: '3',
      content: 'Incident',
    },
    {
      width: '1',
      align: 'right',
      content: 'Low',
      defaultSortDirection: SortDirection.DESC,
      comparator: (a, b, sortDirection) => {
        const getValue = (i: Incident) => {
          return pathOr(
            undefined,
            [SentinelPriorityLevel.LOW],
            i.notificationCountsByPriority
          );
        };
        return genericTableSort(
          a,
          b,
          sortDirection,
          SORT_IGNORED_VALUES,
          getValue
        );
      },
    },
    {
      width: '1',
      align: 'right',
      content: 'Med',
      defaultSortDirection: SortDirection.DESC,
      comparator: (a, b, sortDirection) => {
        const getValue = (i: Incident) => {
          return pathOr(
            undefined,
            [SentinelPriorityLevel.MEDIUM],
            i.notificationCountsByPriority
          );
        };
        return genericTableSort(
          a,
          b,
          sortDirection,
          SORT_IGNORED_VALUES,
          getValue
        );
      },
    },
    {
      width: '1',
      align: 'right',
      content: 'High',
      defaultSortDirection: SortDirection.DESC,
      comparator: (a, b, sortDirection) => {
        const getValue = (i: Incident) => {
          return pathOr(
            undefined,
            [SentinelPriorityLevel.HIGH],
            i.notificationCountsByPriority
          );
        };
        return genericTableSort(
          a,
          b,
          sortDirection,
          SORT_IGNORED_VALUES,
          getValue
        );
      },
    },
    {
      width: '1',
      align: 'right',
      content: 'Crit',
      defaultSortDirection: SortDirection.DESC,
      comparator: (a, b, sortDirection) => {
        const getValue = (i: Incident) => {
          return pathOr(
            undefined,
            [SentinelPriorityLevel.CRITICAL],
            i.notificationCountsByPriority
          );
        };
        return genericTableSort(
          a,
          b,
          sortDirection,
          SORT_IGNORED_VALUES,
          getValue
        );
      },
    },
    {
      width: '2',
      align: 'right',
      content: 'Start Time',
      defaultSortDirection: SortDirection.DESC,
      comparator: (a, b, sortDirection) => {
        const getValue = (i: Incident) => {
          return Date.parse(i.createdAt);
        };
        return genericTableSort(
          a,
          b,
          sortDirection,
          SORT_IGNORED_VALUES,
          getValue
        );
      },
    },
    {
      width: '2',
      align: 'right',
      content: 'End Time',
      defaultSortDirection: SortDirection.DESC,
      comparator: (a, b, sortDirection) => {
        const getValue = (i: Incident) => {
          if (!i.recoveredAt) return undefined;
          return Date.parse(i.recoveredAt);
        };
        return genericTableSort(
          a,
          b,
          sortDirection,
          SORT_IGNORED_VALUES,
          getValue
        );
      },
    },
    {
      width: '2',
      align: 'center',
      content: 'Status',
      comparator: (a, b, sortDirection) => {
        const getStatus = (i: Incident) => {
          if (i.resolved === true) {
            return 'Resolved';
          }
          return 'Active';
        };
        return genericTableSort(
          a,
          b,
          sortDirection,
          SORT_IGNORED_VALUES,
          getStatus
        );
      },
    },
    {
      align: 'center',
      width: '1',
      content: 'Dismiss',
    },
  ];

  const multiSiteHeader: CardListHeader<Incident>[] = [
    ...singleSiteHeader.slice(0, 2),
    {
      width: '2',
      content: 'Site',
      comparator: (a, b, sortDirection) => {
        const getSiteName = (i: Incident) => {
          return pathOr(undefined, [i.siteId, 'title'], sitesById);
        };
        return genericTableSort(
          a,
          b,
          sortDirection,
          SORT_IGNORED_VALUES,
          getSiteName
        );
      },
    },
    ...singleSiteHeader.slice(2),
  ];

  const mobileHeader: CardListHeader<Incident>[] = [
    {
      width: '6',
      content: 'Incident',
    },
    {
      width: '3',
      content: 'Start Time',
    },
    {
      width: '3',
      content: 'End Time',
    },
  ];

  const normalHeader =
    pageType === IncidentsListPageType.MULTI_SITE
      ? multiSiteHeader
      : singleSiteHeader;
  const header = isMobile ? mobileHeader : normalHeader;
  ///*** End CardList Headers ***///

  return (
    <>
      <div className={styles.bigNumbersContainer}>
        <BigNumber
          value={incidentsCount}
          unit=""
          title="Number of Incidents"
          bold
        />
        <BigNumberDivider />
      </div>
      <CardList
        noDefaultSortColumn
        numberOfGridColumns={numberOfGridColumns}
        data={data}
        header={header}
        headerClassName={isMobile ? styles.mobileHeader : ''}
        usePageSelectControl
        pageSelectTopRef={listPageRef}
        renderLimit={10}
        groupHeaders={isMobile ? undefined : groupHeaders}
      />
    </>
  );
};

//Local functions
const getEquipmentId = (resourcePathsById: PathsById, sensorId?: string) => {
  const resourcePath: ResourcePath[] = pathOr(
    [],
    [sensorId],
    resourcePathsById
  );
  const equipment = resourcePath.find((p) => p.type === ResourceType.EQUIPMENT);
  if (equipment !== undefined) return equipment.id;
  return -1;
};

export const renderTime = (
  isoString: string | undefined,
  locale: Locale,
  textAlign: 'left' | 'right',
  ianaTimeZoneCode?: string,
  showTimezone?: boolean
) => {
  if (!isoString) return global.NOT_AVAILABLE;

  const date = parseISO(isoString);
  const formattedDate = determineTimeStringByTimezone(
    date,
    locale.dateFormat,
    ianaTimeZoneCode
  );
  const formattedTime = determineTimeStringByTimezone(
    date,
    locale.timeFormat,
    ianaTimeZoneCode,
    showTimezone
  );
  return (
    <div
      className={textAlign === 'left' ? styles.leftAlign : styles.rightAlign}
    >
      <div className={styles.title}>{formattedTime}</div>
      <div>{formattedDate}</div>
    </div>
  );
};

const renderStatus = (status: boolean) => {
  if (status === false) {
    return <div className={styles.activeText}>Active</div>;
  } else if (status === true) {
    return <div className={styles.resolvedText}>Resolved</div>;
  } else return global.NOT_AVAILABLE;
};

const renderMostRecentValue = (incident: Incident) => {
  const acceptableSentinelTypes = [
    SentinelType.TEMPERATURE,
    SentinelType.HUMIDITY,
    SentinelType.BINARY,
    SentinelType.CONNECTIVITY,
    SentinelType.GATEWAY_ONLINE_STATUS,
    SentinelType.ENERGYPRO_ONLINE_STATUS,
  ];
  const isAcceptableSentinelType = acceptableSentinelTypes.includes(
    incident.sentinelType
  );
  const isIncidentActive = incident.resolved === false;
  const { sensorId, sentinelType } = incident;

  if (isIncidentActive && isAcceptableSentinelType && sensorId) {
    return (
      <div className={styles.currentValue}>
        <div className={styles.currentValueTitle}>Most Recent Value: </div>
        <IncidentValueChip sensorId={+sensorId} sentinelType={sentinelType} />
      </div>
    );
  }

  return null;
};

export default IncidentsCardList;
